import React, { useState, useEffect, useMemo, useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import PyramidChart from "./components/Charts/PyramidChart";
import MetricsChart from "./components/Charts/MetricsChart";
import HomeFilters from "./components/Filters/HomeFilters";
import { useAuth } from "../../../AuthContext";
import { formatInTimeZone } from "date-fns-tz";
import LeakIcon from "../Maps/Leak/components/LeakIcon";
import Loading from "../components/Loading/Loading";
import "../../static/styles/home.css";
import InfoCards from "./components/Charts/InfoCards";
import { divIcon } from "leaflet";
import HomeMap from "./HomeMap";
import URLS from "../../urls";
import { useTheme, ThemeProvider } from "@mui/material";
import { addDays } from "date-fns";
import { Container, Grid, Box, Paper } from "@mui/material";
import Carousel from "react-material-ui-carousel";

function Home() {
  const theme = useTheme();
  const { axiosInstance, userProfile } = useAuth();
  const [filterData, setFilterData] = useState([{}]);
  const [pipeData, setPipeData] = useState([]);
  const [observationData, setObservationData] = useState(null);
  const [pyramidData, setPyramidData] = useState([]);
  const [AOIGeoJSON, setAOIGeoJSON] = useState(null);
  const [leakSelected, setLeakSelected] = useState("default");
  const [metrics, setMetrics] = useState(null);
  const [customAOI, setCustomAOI] = useState(false);
  const [homeMapMarkers, setHomeMapMarkers] = useState([]);
  const [filteredMarkers, setFilteredMarkers] = useState([]);
  const [selectedAOIs, setSelectedAOIs] = useState([]);
  const [dateRangeSelected, setDateRangeSelected] = useState({
    startDate: new Date("2000-01-01"),
    endDate: new Date(),
  });
  const mapCenter = useMemo(() => {
    return userProfile?.profile?.center?.coordinates
      ? [
          userProfile.profile.center.coordinates[1],
          userProfile.profile.center.coordinates[0],
        ]
      : [0, 0];
  }, [userProfile]);

  const handleSeveritySelected = (severity) => {
    let filtered = homeMapMarkers.filter((marker) =>
      severity
        ? marker.properties.leak_severity === severity
        : marker.properties.leak_status === 4
    );
    setFilteredMarkers(filtered);
  };

  const handleStatusSelected = (selectedMetric, status) => {
    let filtered = filteredMarkers;
    switch (selectedMetric) {
      case "default":
        setFilteredMarkers(homeMapMarkers);
        return;
      case "total_leaks_per_state":
        filtered = homeMapMarkers.filter(
          (marker) => marker.properties.leak_status === status
        );
        setFilteredMarkers(filtered);
        return;
      case "total_volume_per_leak":
        filtered = homeMapMarkers.filter(
          (marker) => marker.properties.identifier === status
        );
        setFilteredMarkers(filtered);
        return;
      case "total_area_per_leak":
        filtered = homeMapMarkers.filter(
          (marker) => marker.properties.identifier === status
        );
        setFilteredMarkers(filtered);
        return;
      default:
        return null;
    }
  };

  useEffect(() => {
    fetchHomeData();
  }, [leakSelected, dateRangeSelected, customAOI, selectedAOIs]);

  useEffect(() => {
    if (leakSelected !== "default") {
      onFilterChange(leakSelected, "leak");
    }
  }, [leakSelected]);

  const fetchHomeData = async () => {
    try {
      // TODO: esto tengo que refactorizarlo porque madre que feo
      let filters = [`?leak=${leakSelected}`];
      let aoi_filter = "";
      if (dateRangeSelected) {
        const { startDate, endDate } = dateRangeSelected;
        if (startDate) {
          filters.push(
            `start=${formatInTimeZone(startDate, "UTC", "yyyy-MM-dd")}`
          );
        }
        if (endDate) {
          filters.push(
            `end=${formatInTimeZone(addDays(endDate, 1), "UTC", "yyyy-MM-dd")}`
          );
        }
      }
      if (customAOI) {
        aoi_filter += `geom=${customAOI ? customAOI : "default"}`;
        filters.push(`geom=${customAOI}`);
      }
      if (selectedAOIs.length > 0) {
        const aoiIds = selectedAOIs.map((aoi) => aoi.id).join(",");
        aoi_filter += `aoi=${aoiIds}`;
        filters.push(`aoi=${aoiIds}`);
      }
      let map_filter = `?${[aoi_filter].filter(Boolean).join("&")}`;
      let filter = `${filters.join("&")}`;
      const [
        leakSelectResponse,
        metricsResponse,
        markersResponse,
        aoiResponse,
        pipeResponse,
      ] = await Promise.all([
        axiosInstance.get(URLS.LEAK.SELECT_DATA),
        axiosInstance.get(URLS.OBSERVATION.METRICS + filter),
        axiosInstance.get(URLS.LEAK.IDS_AND_POSITION + map_filter),
        axiosInstance.get(URLS.AOI.MAIN),
        axiosInstance.get(URLS.PIPE.MAIN),
      ]);

      if (leakSelected === "default") {
        const observationResponse = await axiosInstance.get(
          URLS.OBSERVATION.MAIN + "?fields=volume, severity" + filter
        );
        setObservationData(observationResponse.data.results.features);
      }

      const leakResponseData = leakSelectResponse.data;
      setPyramidData(metricsResponse.data.total_leaks_per_severity);
      setMetrics(metricsResponse.data);
      setPipeData(pipeResponse.data.features);
      const markers = parseLeakToMarkers(markersResponse.data.features);
      setHomeMapMarkers(markers);
      let filteredMarkers;
      if (leakSelected !== "default") {
        filteredMarkers = markers.filter(
          (marker) => marker.id === leakSelected
        );
      } else {
        filteredMarkers = markers;
      }
      setFilteredMarkers(filteredMarkers);
      const filteredLeakData = leakResponseData.filter((leak) =>
        filteredMarkers.some((marker) => marker.id === leak.id)
      );

      setFilterData([
        {
          data: filteredLeakData.length ? leakResponseData : filteredLeakData,
          type: "leak",
          selected: filteredLeakData.length
            ? null
            : leakSelected !== "default"
            ? leakSelected
            : null,
        },
      ]);
      setAOIGeoJSON(aoiResponse.data.features);
    } catch (error) {
      console.error("Error fetching home data", error);
    }
  };

  const parseLeakToMarkers = (markers) => {
    return markers.map((leak) => {
      const leakIconElement = (
        <ThemeProvider theme={theme}>
          <LeakIcon
            status={leak.properties.leak_status}
            severity={leak.properties.leak_severity}
          />
        </ThemeProvider>
      );
      const leakIconHtml = renderToStaticMarkup(leakIconElement);

      const icon = divIcon({
        html: leakIconHtml,
        className: "custom-marker",
        iconSize: [25, 25],
      });
      return {
        ...leak,
        icon,
        type: "LEAK",
      };
    });
  };

  const onFilterChange = async (featureId, type) => {
    // TODO: lo dejo con el switch por si hay mas tipos de filtro en un futuro
    switch (type) {
      case "leak":
        if (featureId && featureId !== "default") {
          setLeakSelected(featureId);
          const leakHomeMapMarkers = homeMapMarkers.filter(
            (marker) => marker.id === featureId
          );
          let prevFilters = JSON.parse(
            sessionStorage.getItem("selectedFilters")
          );
          let newFilters = { ...prevFilters, leak: leakHomeMapMarkers[0] };
          sessionStorage.setItem("selectedFilters", JSON.stringify(newFilters));
          setFilteredMarkers(leakHomeMapMarkers);
          if (observationData && observationData.length > 0) {
            const filteredObservationData = observationData.filter(
              (observation) => observation.properties.leak === featureId
            );
            setObservationData(filteredObservationData);
          } else {
            const observationResponse = await axiosInstance.get(
              `${URLS.OBSERVATION.MAIN}?fields=volume,severity&leak=${featureId}`
            );
            setObservationData(observationResponse.data.results.features);
          }
        } else {
          setLeakSelected("default");
          setFilteredMarkers(homeMapMarkers);
        }
        break;
      default:
        break;
    }
  };

  const onDateRangeSelected = (range) => {
    setDateRangeSelected(range);
  };

  const onAOISelected = (selectedAOIIds) => {
    const selectedAOIObjects = AOIGeoJSON.filter((aoi) =>
      selectedAOIIds.includes(aoi.id)
    );
    setSelectedAOIs(selectedAOIObjects);
  };

  const memoizedPyramidData = useMemo(() => pyramidData, [pyramidData]);
  const memoizedMetrics = useMemo(() => metrics, [metrics]);
  const memoizedFilterData = useMemo(() => filterData, [filterData]);
  const memoizedObservationData = useMemo(
    () => observationData,
    [observationData]
  );

  return (
    <Container maxWidth="bg">
      <Box
        sx={{
          height: "100vh", // Ocupa toda la altura de la pantalla
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 2,
        }}
      >
        {observationData ? (
          <>
            <Paper
              elevation={3}
              sx={{
                width: "100%",
                height: "100%",
                boxShadow: 3,
                padding: 2,
                borderRadius: "15px",
              }}
            >
              <Grid container spacing={3}>
                <Grid item display="grid" xs={12}>
                  <HomeFilters
                    onFilterChange={onFilterChange}
                    filterData={memoizedFilterData}
                    observationData={memoizedObservationData}
                    onDateRangeSelected={onDateRangeSelected}
                    onAOISelected={onAOISelected}
                    AOIData={AOIGeoJSON}
                  />
                </Grid>
                <Grid position={"relative"} item xs={12}>
                  <MetricsChart
                    data={memoizedMetrics}
                    onSelectedStatus={handleStatusSelected}
                  />
                  <Carousel autoPlay={false} animation="slide">
                    <PyramidChart
                      dataSource={memoizedPyramidData}
                      handleSeveritySelected={handleSeveritySelected}
                    />
                    <InfoCards dataSource={memoizedObservationData} />
                  </Carousel>
                </Grid>
              </Grid>
            </Paper>

            <HomeMap
              initialCenter={mapCenter}
              markers={filteredMarkers}
              setCustomAOI={setCustomAOI}
              AOIGeoJSON={AOIGeoJSON}
              leakSelected={leakSelected}
              pipes={pipeData}
            />
          </>
        ) : (
          <Loading />
        )}
      </Box>
    </Container>
  );
}

export default Home;
